import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { roleAtLeast, type ResourceRole } from '@os/shared';
import { useApi } from '../../app/providers';
import type { CommentAnchor } from '../../lib/comment-types';
import type { SyncProvider } from '../../lib/sync/provider';

export type CommentSync = Pick<SyncProvider, 'notifyComments' | 'onComments'>;

export const commentKeys = {
  threads: (boardId: string) => ['threads', boardId] as const,
  thread: (threadId: string) => ['thread', threadId] as const,
  mentionables: (boardId: string) => ['mentionables', boardId] as const,
};

/** What the comment UI offers for a role (§9.2, P7 Ruling 3); the RPCs enforce the same. */
export function commentRights(role: ResourceRole, linkOnly: boolean) {
  return {
    canComment: roleAtLeast(role, 'commenter'),
    canResolveAny: roleAtLeast(role, 'editor'),
    canResolveOwn: roleAtLeast(role, 'commenter') && !linkOnly,
    canDeleteAny: roleAtLeast(role, 'manager'),
  };
}

export function useThreads(boardId: string) {
  const api = useApi();
  return useQuery({ queryKey: commentKeys.threads(boardId), queryFn: () => api.listThreads(boardId) });
}

export function useThreadMessages(threadId: string | null) {
  const api = useApi();
  return useQuery({ queryKey: commentKeys.thread(threadId ?? ''), queryFn: () => api.threadMessages(threadId!), enabled: !!threadId });
}

export function useMentionables(boardId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({ queryKey: commentKeys.mentionables(boardId), queryFn: () => api.mentionables(boardId), enabled, staleTime: 60_000 });
}

/** Another connection changed a thread: reload the list and that thread. */
export function useRoomComments(boardId: string, sync: CommentSync | null) {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      sync?.onComments((threadId) => {
        void queryClient.invalidateQueries({ queryKey: commentKeys.threads(boardId) });
        void queryClient.invalidateQueries({ queryKey: commentKeys.thread(threadId) });
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }),
    [sync, boardId, queryClient],
  );
}

type Write =
  | { kind: 'create'; anchor: CommentAnchor; body: string }
  | { kind: 'reply'; threadId: string; body: string }
  | { kind: 'edit'; threadId: string; id: string; body: string }
  | { kind: 'delete'; threadId: string; id: string }
  | { kind: 'resolve'; threadId: string; resolved: boolean }
  | { kind: 'deleteThread'; threadId: string };

/** Every comment write: then the list and the thread reload here, and the room tells the others. */
export function useCommentWrite(boardId: string, sync: CommentSync | null) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (w: Write): Promise<string> => {
      switch (w.kind) {
        case 'create':
          return api.createThread({ boardId, anchor: w.anchor, body: w.body });
        case 'reply':
          await api.replyComment(w.threadId, w.body);
          return w.threadId;
        case 'edit':
          await api.editComment(w.id, w.body);
          return w.threadId;
        case 'delete':
          await api.deleteComment(w.id);
          return w.threadId;
        case 'resolve':
          await api.setThreadResolved(w.threadId, w.resolved);
          return w.threadId;
        case 'deleteThread':
          await api.deleteThread(w.threadId);
          return w.threadId;
      }
    },
    onSuccess: async (threadId) => {
      sync?.notifyComments(threadId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: commentKeys.threads(boardId) }),
        queryClient.invalidateQueries({ queryKey: commentKeys.thread(threadId) }),
      ]);
    },
  });
}
